import { Video, ShieldAlert, Circle, MonitorPlay } from "lucide-react";

const LABEL = {
  tab_switch: "Tab switch",
  focus_loss: "Focus lost",
  fullscreen_exit: "Exited fullscreen",
  copy: "Copy",
  paste: "Paste",
  right_click: "Context menu",
  camera_blocked: "Camera blocked",
  no_face: "No face",
  multiple_faces: "Multiple people",
  looking_away: "Looking away",
  mic_blocked: "Mic unavailable",
  screen_share_stopped: "Screen share stopped",
  screen_blocked: "Screen not shared",
  talking: "Talking detected",
  background_noise: "Background noise",
  multiple_voices: "Multiple voices",
  phone_detected: "Phone visible",
};

export function ProctorLive({ videoRef, screenRef, events = [], status, screenStatus = "off", micStatus = "off", audioLevel = 0, audioSuspended, recording, deviceErrors = {}, retryDevices, resumeAudio, uploadStatus }) {
  const showScreen = screenStatus && screenStatus !== "off";
  return (
    <div
      className="rounded-2xl border border-amber-500/20 bg-[#0A0B0E]/60 backdrop-blur-md p-3 space-y-3 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
      data-testid="proctor-live"
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-300">
          <Video className="h-3.5 w-3.5" /> Device monitoring
        </span>
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-rose-400" data-testid="proctor-rec">
          <Circle className={`h-2 w-2 ${recording ? "fill-rose-500 text-rose-500 animate-pulse" : "text-muted-foreground"}`} /> {recording ? "REC" : "NOT RECORDING"}
        </span>
      </div>

      <div className="space-y-2 text-xs text-slate-200" data-testid="device-readiness">
        <div className="flex flex-wrap justify-between gap-2"><span data-testid="camera-status">Camera: {status === "off" ? "not requested" : status}</span><span data-testid="microphone-status">Microphone: {micStatus === "off" ? "not requested" : micStatus}</span></div>
        {micStatus === "active" && <div className="h-2 rounded-full bg-white/10 overflow-hidden" role="meter" aria-label="Microphone input level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={audioLevel} data-testid="microphone-level"><div className="h-full bg-emerald-400 transition-[width]" style={{width:`${audioLevel}%`}} /></div>}
        {Object.entries(deviceErrors).filter(([,v])=>v).map(([k,v]) => <p key={k} role="status" data-testid={`device-error-${k}`} className="text-amber-200">{k}: {v}</p>)}
        {(status === "error" || micStatus === "error") && <button onClick={retryDevices} data-testid="retry-devices" className="rounded-lg bg-white/10 px-3 py-2 hover:bg-white/20">Retry camera & microphone</button>}
        {audioSuspended && <button onClick={resumeAudio} data-testid="resume-audio" className="rounded-lg bg-white/10 px-3 py-2 hover:bg-white/20">Enable microphone analysis</button>}
        {uploadStatus && <p data-testid="proctor-upload-status">{uploadStatus}</p>}
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-black">
          <video ref={videoRef} muted playsInline className="h-full w-full object-cover" data-testid="proctor-webcam" />
          {status === "error" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/75 text-[11px] text-rose-300 text-center px-3">
              Camera unavailable. Check the device message above and retry. This is an advisory signal, not a rejection.
            </div>
          )}
          {status === "active" && (
            <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-mono text-emerald-300">
              <Circle className="h-1.5 w-1.5 fill-emerald-400 text-emerald-400" /> live
            </div>
          )}
        </div>

        <div className="rounded-xl border border-white/10 bg-[#111319]/80 p-2 max-h-[150px] overflow-y-auto" data-testid="proctor-events">
          {events.length === 0 ? (
            <div className="text-[11px] text-muted-foreground p-2 flex items-center gap-1.5">
              <ShieldAlert className="h-3.5 w-3.5 text-emerald-400" /> Monitoring — no flags so far.
            </div>
          ) : (
            events.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-[11px] py-1 border-b border-white/5 last:border-0">
                <span className="text-amber-200">{LABEL[e.type] || e.type}</span>
                <span className="font-mono text-muted-foreground">{new Date(e.at).toLocaleTimeString()}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {showScreen && (
        <div className="relative aspect-video rounded-xl overflow-hidden border border-white/10 bg-black" data-testid="proctor-screen-tile">
          <video ref={screenRef} muted playsInline className="h-full w-full object-contain bg-black" data-testid="proctor-screen" />
          {screenStatus === "active" ? (
            <div className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-mono text-emerald-300">
              <MonitorPlay className="h-3 w-3" /> screen recording
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-black/75 text-[11px] text-amber-300 text-center px-3">
              <span className="inline-flex items-center gap-1.5"><MonitorPlay className="h-3.5 w-3.5" /> {screenStatus === "error" ? "Screen share stopped — logged for review" : "Waiting for screen share…"}</span>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        These signals are <strong>advisory only</strong> and reviewed by a human. Nothing here auto-rejects your application.
      </p>
    </div>
  );
}
